import { createTheme, Theme } from "@mui/material";
import createTinyTheme from "./createTheme";
import { FontWeight } from "./configs/typography";
import "./types";

const FONT_FAMILY = "'Inter', 'Helvetica', 'Arial', sans-serif";

// SamCart neutral palette (Sushi design system)
const N = {
  0: "#FFFFFF",
  10: "#F9FBFC",
  20: "#F5F8FA", // N50  — disabled bg, alt background
  30: "#EFF1F3", // N100 — light borders
  40: "#DEE3E7", // N200 — borders, table dividers
  50: "#BDC7CF", // N300 — switch track
  60: "#9CABB7", // N400 — disabled text, muted borders
  70: "#7B8F9E", // N500 — switch unchecked, secondary icons
  80: "#5A7386", // N600 — secondary text, tab text
  90: "#233543", // N700 — primary text
  100: "#0B1A26", // N800 — darkest text, tooltips
};

const createSamCartTheme = (): Theme => {
  const base = createTinyTheme();
  return createTheme(base, {
    palette: {
      primary: {
        // Named shades
        lighter: "#F0F8FF", // B50  — hover background
        light: "#E0F2FF", // B100 — selected/chip background
        main: "#046BD9", // B600 — buttons, links
        dark: "#0357B6", // B700 — hover on contained
        active: "#024291", // B800 — pressed state
        contrastText: "#FFFFFF",
        // Full numeric shade set (used directly by component style overrides,
        // e.g. Calendar/RowHelpers/EmailContent/SenderDetails). createTheme's
        // deep merge only overwrites keys present here — leaving any of
        // these out keeps the base tiny-theme's purple for that shade.
        // 10/20/40/60/80/120 are interpolated between the named anchors
        // below and should be confirmed against the Sushi design tokens.
        10: "#FBFDFF",
        20: "#F8FBFF",
        40: "#F3F9FF",
        50: "#F0F8FF",
        60: "#EAF6FF",
        75: "#E0F2FF",
        80: "#B4D7F7",
        100: "#046BD9",
        120: "#035BBD",
        125: "#0357B6",
      },
      secondary: {
        lighter: "#F5F8FA", // N50
        light: "#EFF1F3", // N100
        main: "#233543", // N700
        dark: "#0B1A26", // N800
        active: "#111111", // N900
        contrastText: "#FFFFFF",
      },
      error: {
        lighter: "#FFE5E8", // R100
        light: "#FFBFC4", // R200
        main: "#D91636", // R700
        dark: "#B20925", // R800
        active: "#99001A", // R900
        contrastText: "#FFFFFF",
      },
      success: {
        lighter: "#E6FAF3", // G100
        light: "#E6FAF3", // G100
        main: "#089B6A", // G700
        dark: "#096F4E", // G800
        contrastText: "#FFFFFF",
      },
      warning: {
        lighter: "#FFF9E5", // Y100
        light: "#FFF9E5", // Y100
        main: "#FFE699", // Y300
        dark: "#BF7F00", // Y900
        contrastText: "#233543", // N700 — dark text on light yellow bg
      },
      text: {
        primary: "#233543", // N700
        secondary: "#5A7386", // N600
      },
      background: {
        paper: "#FFFFFF", // N0
        default: "#F5F8FA", // N50
      },
      // Override grey scale with SamCart neutrals — fixes all component borders,
      // disabled states, dividers, tab text, etc. that reference theme.palette.grey[*]
      grey: N,
    },
    shadows: [
      "none",
      // Light 1 — cards, default surfaces
      "0px 0px 1px 0px rgba(17,17,17,0.15), 0px 1px 5px 0px rgba(17,17,17,0.15)",
      // Light 2 — hover/active for cards
      "0px 0px 5px 0px rgba(17,17,17,0.15), 0px 3px 10px 0px rgba(17,17,17,0.20)",
      // Light 3 — modals, panels, overlays
      "0px 8px 24px 0px rgba(17,17,17,0.10)",
      // Reuse Light 3 for higher elevations
      "0px 8px 24px 0px rgba(17,17,17,0.10)",
      "0px 8px 24px 0px rgba(17,17,17,0.10)",
      "0px 8px 24px 0px rgba(17,17,17,0.10)",
      "0px 8px 24px 0px rgba(17,17,17,0.10)",
      "0px 8px 24px 0px rgba(17,17,17,0.10)",
      "0px 8px 24px 0px rgba(17,17,17,0.10)",
      "0px 8px 24px 0px rgba(17,17,17,0.10)",
      "0px 8px 24px 0px rgba(17,17,17,0.10)",
      "0px 8px 24px 0px rgba(17,17,17,0.10)",
      "0px 8px 24px 0px rgba(17,17,17,0.10)",
      "0px 8px 24px 0px rgba(17,17,17,0.10)",
      "0px 8px 24px 0px rgba(17,17,17,0.10)",
      "0px 8px 24px 0px rgba(17,17,17,0.10)",
      "0px 8px 24px 0px rgba(17,17,17,0.10)",
      "0px 8px 24px 0px rgba(17,17,17,0.10)",
      "0px 8px 24px 0px rgba(17,17,17,0.10)",
      "0px 8px 24px 0px rgba(17,17,17,0.10)",
      "0px 8px 24px 0px rgba(17,17,17,0.10)",
      "0px 8px 24px 0px rgba(17,17,17,0.10)",
      "0px 8px 24px 0px rgba(17,17,17,0.10)",
      "0px 8px 24px 0px rgba(17,17,17,0.10)",
    ] as Theme["shadows"],
    typography: {
      fontFamily: FONT_FAMILY,
      // Override every variant — base theme bakes "Archivo" into each one at creation time
      // so overriding fontFamily alone doesn't cascade to pre-built variants
      priceH0: { fontFamily: FONT_FAMILY },
      priceH0R: { fontFamily: FONT_FAMILY },
      h0: { fontFamily: FONT_FAMILY },
      h1: { fontFamily: FONT_FAMILY },
      h2: { fontFamily: FONT_FAMILY },
      h4: { fontFamily: FONT_FAMILY },
      h4R: { fontFamily: FONT_FAMILY },
      p1: { fontFamily: FONT_FAMILY },
      p2: { fontFamily: FONT_FAMILY },
      p3: { fontFamily: FONT_FAMILY },
      label1: { fontFamily: FONT_FAMILY },
      label1R: { fontFamily: FONT_FAMILY },
      label2: { fontFamily: FONT_FAMILY },
      label2R: { fontFamily: FONT_FAMILY },
      label3: { fontFamily: FONT_FAMILY },
      label3R: { fontFamily: FONT_FAMILY },
      // MUI built-in variants
      button: { fontFamily: FONT_FAMILY },
      caption: { fontFamily: FONT_FAMILY },
      overline: { fontFamily: FONT_FAMILY },
      h3: {
        fontFamily: FONT_FAMILY,
        fontWeight: 600,
        fontSize: "18px",
        lineHeight: "24px",
        letterSpacing: "-1px",
        "@media (min-width: 964px)": {
          fontSize: "20px",
          lineHeight: "24px",
          letterSpacing: "-0.02em",
        },
        "@media (min-width: 1792px)": {
          fontSize: "28px",
          lineHeight: "32px",
          letterSpacing: "-1.5px",
        },
      },
      body1: {
        fontFamily: FONT_FAMILY,
        fontWeight: 400,
        fontSize: "16px",
        lineHeight: "22px",
        letterSpacing: "-1.5px",
        "@media (min-width: 964px)": {
          fontSize: "20px",
          lineHeight: "24px",
          letterSpacing: "-0.02em",
        },
      },
    },
    components: {
      MuiChip: {
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) => ({
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
            fontWeight: FontWeight.bold,
          }),
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) => ({
            "&.MuiAlert-outlinedInfo": {
              backgroundColor: theme.palette.primary.lighter,
              borderColor: theme.palette.primary.main,
              "& .MuiAlert-icon": {
                color: theme.palette.primary.dark,
              },
            },
          }),
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: "#0B1A26", // N800
            color: "#FFFFFF",
            fontFamily: FONT_FAMILY,
            fontWeight: 400,
            fontSize: "11px",
            lineHeight: "14px",
            padding: "4px 8px",
            borderRadius: "4px",
          },
          arrow: {
            color: "#0B1A26",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          containedPrimary: {
            "&:hover": { backgroundColor: "#0357B6" }, // B700
            "&:active": { backgroundColor: "#024291" }, // B800
          },
          containedSecondary: {
            "&:hover": { backgroundColor: "#0B1A26" }, // N800
            "&:active": { backgroundColor: "#111111" }, // N900
          },
          outlinedPrimary: {
            borderColor: "#046BD9",
            "&:hover": {
              backgroundColor: "#F0F8FF",
              borderColor: "#0357B6",
              color: "#0357B6",
            },
            "&:active": {
              backgroundColor: "#F0F8FF",
              borderColor: "#024291",
              color: "#024291",
            },
          },
          outlinedSecondary: {
            borderColor: "#233543",
            "&:hover": {
              backgroundColor: "#F5F8FA",
              borderColor: "#0B1A26",
              color: "#0B1A26",
            },
            "&:active": {
              backgroundColor: "#F5F8FA",
              borderColor: "#111111",
              color: "#111111",
            },
          },
          textPrimary: {
            "&:hover": { backgroundColor: "#F0F8FF", color: "#0357B6" },
            "&:active": { backgroundColor: "#F0F8FF", color: "#024291" },
          },
          textSecondary: {
            "&:hover": { backgroundColor: "#F5F8FA", color: "#0B1A26" },
            "&:active": { backgroundColor: "#F5F8FA", color: "#111111" },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: {
            borderColor: "#DEE3E7", // N200
          },
          root: {
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#9CABB7", // N400
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#046BD9", // B600 primary
            },
            "&.Mui-error .MuiOutlinedInput-notchedOutline": {
              borderColor: "#D91636", // R700
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            border: "1px solid #DEE3E7", // N200 — SamCart card border
          },
          elevation0: {
            border: "none",
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            "&:nth-of-type(even)": {
              backgroundColor: "rgba(11,26,38,0.04)", // N800 @ 4%
            },
            "&:hover": {
              backgroundColor: "#F5F8FA", // N50
            },
          },
        },
      },
    },
  });
};

export default createSamCartTheme;
